import { Link } from "react-router-dom";
import { Dropdown, Nav, Spinner } from "react-bootstrap";

import type { AuthUserProps } from "src/types/auth";

interface UserMenuProps {
  userData: AuthUserProps;
  isSuperAdmin: boolean;
  builderPath: string;
  onResetDashboardStore: () => void;
  openKey: string | null;
  onOpen: (key: string) => void;
  onScheduleClose: (key: string) => void;
  onSetOpenKey: (key: string | null) => void;
  onLogout: () => void;
  isLoggingOut: boolean;
}

const DROPDOWN_KEY = "user-dropdown";

/**
 * The header's user dropdown: dashboard builder link, role-gated
 * User Management / Admin Portal shortcuts, change password, logout.
 */
export default function UserMenu({
  userData,
  isSuperAdmin,
  builderPath,
  onResetDashboardStore,
  openKey,
  onOpen,
  onScheduleClose,
  onSetOpenKey,
  onLogout,
  isLoggingOut,
}: UserMenuProps) {
  return (
    <Dropdown
      as="li"
      className="nav-item"
      show={openKey === DROPDOWN_KEY}
      onMouseEnter={() => onOpen(DROPDOWN_KEY)}
      onMouseLeave={() => onScheduleClose(DROPDOWN_KEY)}
      onToggle={(nextShow) => onSetOpenKey(nextShow ? DROPDOWN_KEY : null)}
    >
      <Dropdown.Toggle id={DROPDOWN_KEY} as={Nav.Link}>
        <i className="user-icon bi bi-person"></i>
        {[userData?.firstname, userData?.lastname]
          .filter(Boolean)
          .join(" ") || userData?.name}
      </Dropdown.Toggle>

      <Dropdown.Menu
        renderOnMount
        onMouseEnter={() => onOpen(DROPDOWN_KEY)}
        onMouseLeave={() => onScheduleClose(DROPDOWN_KEY)}
        popperConfig={{
          modifiers: [
            {
              name: "offset",
              options: { offset: [0, 12] },
            },
          ],
        }}
      >
        <Dropdown.Item
          as={Link}
          to={builderPath}
          onClick={() => onResetDashboardStore()}
        >
          Dashboard Builder
        </Dropdown.Item>
        {userData?.admin && !isSuperAdmin && (
          <Dropdown.Item as={Link} to="/user-management">
            User Management
          </Dropdown.Item>
        )}
        <Dropdown.Item as={Link} to="/change-password">
          Change Password
        </Dropdown.Item>
        {isSuperAdmin && (
          <>
            <Dropdown.Divider />
            <Dropdown.Header>Admin Portal</Dropdown.Header>
            <Dropdown.Item as={Link} to="/portal/dashboards">
              Dashboards
            </Dropdown.Item>
            <Dropdown.Item as={Link} to="/portal/users">
              Users
            </Dropdown.Item>
            <Dropdown.Item as={Link} to="/portal/clients">
              Clients
            </Dropdown.Item>
            <Dropdown.Item as={Link} to="/portal/programs">
              Programs
            </Dropdown.Item>
            <Dropdown.Item as={Link} to="/portal/tiers">
              Tiers
            </Dropdown.Item>
          </>
        )}
        <Dropdown.Divider />
        <Dropdown.Item
          as="button"
          className="logout-btn"
          onClick={onLogout}
          disabled={isLoggingOut}
        >
          {isLoggingOut ? (
            <>
              <Spinner animation="border" size="sm" className="me-1" />{" "}
              Signing out…
            </>
          ) : (
            "Logout"
          )}
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
}
